import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Staged changes, across the top of every tab — the always-visible awareness
 * AlertBanner already gives channel problems, for the queue instead.
 *
 * The one place to push the queue. Products' own "Sync now" reads from the
 * channels; this is the only button that writes to them, from any tab, so
 * there is exactly one "sync" verb in the app rather than two doing opposite
 * things under the same name.
 *
 * Deliberately not a sidebar: the product table already scrolls horizontally
 * (a "listed" column per marketplace, a price column per channel), and there
 * is no sidebar/drawer pattern anywhere else in the app to extend. A bar plus
 * a full tab for the queue itself reuses what AlertBanner and HistoryTab
 * already established rather than inventing a new page shell.
 */

export function PendingBar({ onOpenPending }: { onOpenPending: () => void }) {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['queue'],
    queryFn: api.queue,
    // Polling only matters while a batch might be mid-push, or while a push
    // that already left the queue is still settling in the background — the
    // interval is short enough that "Sync Changes" feels live without
    // hammering the server the rest of the time this sits idle.
    refetchInterval: (query) => {
      if (query.state.data?.entries.some((entry) => entry.status === 'executing')) return 2000;
      if ((query.state.data?.unsettled ?? 0) > 0) return 15_000;
      return 60_000;
    },
    throwOnError: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const executeMutation = useMutation({
    mutationFn: () => api.executeQueue(),
    onSuccess: () => {
      invalidate();
      onOpenPending();
    },
  });

  // Clearing the queue and dismissing every resolved write are two different
  // rows in two different tables, but one button in this bar — "Clear" is
  // meant to reset everything the tray can honestly reset in one action, not
  // just the staged half of it. A write still actually in flight or still
  // being staged-rechecked is untouched either way; nothing here can force
  // that any more than the single-row dismiss action could.
  const clearMutation = useMutation({
    mutationFn: () => Promise.all([api.clearQueue(), api.dismissAllWrites()]),
    onSuccess: invalidate,
  });

  const dismissAllMutation = useMutation({
    mutationFn: () => api.dismissAllWrites(),
    onSuccess: invalidate,
  });

  const entries = data?.entries ?? [];
  const unsettled = data?.unsettled ?? 0;
  const dismissable = data?.dismissable ?? 0;
  if (entries.length === 0 && unsettled === 0) return null;

  const conflicts = entries.filter((e) => e.status === 'conflict').length;
  const needsConfirmation = entries.filter((e) => e.status === 'needs_confirmation').length;
  const executing = entries.some((e) => e.status === 'executing');

  // Nothing staged, but a pushed write hasn't settled yet — the same state
  // Products' own in-flight group is watching (see ProductTable's
  // inFlightQuery) until a background recheck confirms it, the channel takes
  // it back, or a human dismisses it (WriteStateBadge). Not every one of
  // these is still "awaiting" anything — some are done retrying and just
  // need a look — so this only claims outstanding, not what stage each is at.
  if (entries.length === 0) {
    return (
      <div className="pending-bar">
        <span className="small muted">
          {unsettled} change{unsettled === 1 ? '' : 's'} still outstanding with a channel — see the Products
          tab
        </span>
        <div className="spacer" />
        {/* Only offered once something here has actually stopped retrying —
            a write still in flight or still being staged-rechecked can't be
            dismissed, so this stays hidden rather than sit there doing
            nothing when every outstanding write is still genuinely pending. */}
        {dismissable > 0 && (
          <button
            className="link small"
            disabled={dismissAllMutation.isPending}
            onClick={() => dismissAllMutation.mutate()}
          >
            {dismissAllMutation.isPending
              ? 'Dismissing…'
              : `Dismiss ${dismissable} resolved`}
          </button>
        )}
      </div>
    );
  }

  const ownerBreakdown = () => {
    const byOwner = new Map<string, number>();
    for (const entry of entries) {
      const label =
        entry.source === 'sheet'
          ? 'the Google Sheet'
          : entry.queuedBy?.name || entry.queuedBy?.email || 'an unknown user';
      byOwner.set(label, (byOwner.get(label) ?? 0) + 1);
    }
    return [...byOwner.entries()].map(([who, count]) => `${count} from ${who}`).join(', ');
  };

  const clearAll = () => {
    if (clearMutation.isPending) return;
    const dismissNote =
      dismissable > 0 ? ` and dismiss ${dismissable} resolved write${dismissable === 1 ? '' : 's'}` : '';
    if (
      window.confirm(
        `Clear all ${entries.length} pending changes (${ownerBreakdown()})${dismissNote}? This cannot be undone.`,
      )
    ) {
      clearMutation.mutate();
    }
  };

  /**
   * "Sync all" with nothing selected pushes every staged change, including
   * every row the Google Sheet sync staged — someone clicking this to push
   * their own three edits could otherwise also push two hundred sheet-staged
   * ones without knowing it. Small batches (a person's own handful of edits,
   * the common case) go straight through; anything past a few rows or
   * anything sheet-sourced gets the same breakdown-and-confirm clearAll uses.
   */
  const syncAll = () => {
    if (executeMutation.isPending || executing) return;
    const hasSheetRows = entries.some((e) => e.source === 'sheet');
    if (entries.length > 5 || hasSheetRows) {
      if (!window.confirm(`Push all ${entries.length} pending changes to their channels (${ownerBreakdown()})?`)) {
        return;
      }
    }
    executeMutation.mutate();
  };

  return (
    <div className={`pending-bar${conflicts > 0 || needsConfirmation > 0 ? ' warn' : ''}`}>
      <div>
        <strong>
          {entries.length} pending change{entries.length === 1 ? '' : 's'}
        </strong>
        {conflicts > 0 && (
          <span className="small"> — {conflicts} conflict{conflicts === 1 ? '' : 's'} need review</span>
        )}
        {needsConfirmation > 0 && (
          <span className="small">
            {' '}
            — {needsConfirmation} need{needsConfirmation === 1 ? 's' : ''} confirmation
          </span>
        )}
        {executing && <span className="small muted"> — pushing now…</span>}
        {unsettled > 0 && (
          <span className="small muted">
            {' '}
            — {unsettled} more still outstanding with a channel
          </span>
        )}
      </div>
      <div className="spacer" />
      <button className="link small" onClick={onOpenPending}>
        Review
      </button>
      <button className="link small danger" disabled={clearMutation.isPending} onClick={clearAll}>
        Clear
      </button>
      <button className="primary small" disabled={executeMutation.isPending || executing} onClick={syncAll}>
        {executeMutation.isPending || executing ? 'Syncing…' : `Sync Changes (${entries.length})`}
      </button>
    </div>
  );
}
